// Typed API layer for the CRM "posibles leads duplicados" endpoint consumed by
// the admin SPA (portado del banner/modal del CRM a Operations, ficha del lead).
//
// El CRM detecta POSIBLES duplicados de un lead (misma persona/contacto) por
// tres señales: mismo DNI (fuerte), mismo teléfono (⚠ puede ser un familiar) y
// misma variante de email `+tag`. NO auto-fusiona: en el CRM el asesor confirma
// y fusiona manualmente. En Operations SOLO portamos la DETECCIÓN + listado (la
// fusión es peligrosa y se queda en el CRM).
//
// Contrato del CRM (crm/apps/api GET /leads/:id/duplicates, sin requireRole →
// cualquier sesión CRM autenticada):
//   { count: number, candidates: DuplicateCandidate[] }
//
// Los `id` de cada candidato llegan en Crockford-26 desde el CRM (el plugin los
// serializa con serializeLeadId). Aun así los canonicalizamos en esta capa con
// canonicalLeadIdOrNull por consistencia con el resto de módulos CRM (si algún
// día devolviera hex-32, no romperían los links internos a /leads/:ulid).

import { crmRequest } from "./crm";
import { canonicalLeadIdOrNull } from "./crmLeadId";

// Señal que disparó la sugerencia de duplicado (enum cerrado del CRM). Se mapea
// a un badge en el modal.
export type DuplicateMatchReason = "dni" | "phone" | "email_variant";

// Etiqueta en español de cada criterio de match (mismo espíritu que el CRM).
export const MATCH_REASON_LABEL: Record<DuplicateMatchReason, string> = {
  dni: "Mismo DNI",
  phone: "Mismo teléfono",
  email_variant: "Mismo email",
};

// Candidato a duplicado. Forma exacta del contrato del CRM
// (crm/packages/contracts DuplicateCandidate). `id` canonicalizado a Crockford
// en esta capa para linkar internamente a la ficha de Operations.
export type DuplicateCandidate = {
  id: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  market: string | null;
  productStatus: string | null;
  createdAt: string | null;
  assignedToName: string | null;
  commentCount: number;
  isProspect: boolean;
  // Una o varias señales (el CRM garantiza min(1)).
  matchReasons: DuplicateMatchReason[];
};

export type DuplicatesResponse = {
  count: number;
  candidates: DuplicateCandidate[];
};

// GET /leads/:id/duplicates -> posibles duplicados del lead. El CRM acepta el
// leadId en hex-32 o Crockford-26 en input, así que pasamos `leadUlid` tal cual.
// Nunca 404: un lead desconocido/sin proyectar devuelve { count: 0, candidates:
// [] }, indistinguible de "sin duplicados".
export function getLeadDuplicates(
  leadUlid: string,
): Promise<DuplicatesResponse> {
  return crmRequest<DuplicatesResponse>(
    `/leads/${encodeURIComponent(leadUlid)}/duplicates`,
  ).then((res) => ({
    count: res.count,
    candidates: res.candidates.map((c) => ({
      ...c,
      id: canonicalLeadIdOrNull(c.id) ?? c.id,
    })),
  }));
}
